import React, { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import '../DeliveryBackModal.css';

/**
 * RemoveProductConfirmModal
 *
 * Sprint 6 multi-product. Confirmation modal shown when a trader
 * tries to remove an extra product from a Fuel Enquiry that has
 * already been saved to the database. Reuses the existing
 * DeliveryBackModal CSS so the look/feel matches the rest of the
 * Fuel Enquiry form modals -- only the copy + buttons change.
 *
 * Note: an unsaved (in-memory) extra product card removes silently
 * without showing this modal. The modal is only opened when the row
 * already has a database id (i.e., removing it would delete the
 * FuelEnquiryProduct row + any downstream child data on next save).
 */
const RemoveProductConfirmModal = ({
  productLabel,
  onConfirm,
  onCancel,
  // Optional copy overrides so the same modal can be reused on the
  // Bid/Offer screen (where the deleted child fields differ slightly
  // and the parent isn't an "enquiry"). Both default to the original
  // enquiry-specific wording so existing call-sites don't change.
  // The third-party-cost sentence is deliberately part of the DEFAULT copy, not
  // an extra prop. Any cost row on this deal that was ticked against this
  // product loses that tick when the product goes -- the trader should read
  // that before they confirm, not discover it afterwards on the cost card.
  description = 'Removing it will delete its bunker spec, tonnage, sell price and supplier pricing from the database when you save the enquiry. Any 3rd Party Cost rows ticked against this product will no longer show it; the cost rows themselves stay, and an untagged cost counts against the whole deal. This cannot be undone.',
  contextLabel = 'this enquiry',
}) => {
  const handleModalClick = (e) => e.stopPropagation();

  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, [onCancel]);

  return (
    <div className="delivery-back-modal-overlay" onClick={onCancel}>
      <div className="delivery-back-modal" onClick={handleModalClick}>
        <div className="delivery-back-modal-header">
          <div className="delivery-back-header-content">
            <h3 className="delivery-back-modal-title">
              Remove {productLabel || 'Product'}?
            </h3>
          </div>
          <button
            className="delivery-back-close-btn"
            onClick={onCancel}
            aria-label="Close modal"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="delivery-back-modal-body">
          <div className="delivery-back-warning-banner">
            <FontAwesomeIcon icon={faExclamationTriangle} size="lg" />
            <div className="delivery-back-warning-text">
              <strong>This product is already saved to {contextLabel}</strong>
              <p>{description}</p>
            </div>
          </div>

          <div className="delivery-back-confirm-message">
            <p className="delivery-back-question">
              Are you sure you want to remove {productLabel || 'this product'} from {contextLabel}?
            </p>
          </div>
        </div>

        <div className="delivery-back-modal-footer">
          <button
            className="delivery-back-btn-secondary"
            onClick={onCancel}
          >
            Keep Product
          </button>
          <button
            className="delivery-back-btn-primary"
            onClick={onConfirm}
            style={{ background: 'linear-gradient(135deg, #dc2626, #ef4444)' }}
          >
            <FontAwesomeIcon icon={faTrash} /> Remove Product
          </button>
        </div>
      </div>
    </div>
  );
};

export default RemoveProductConfirmModal;
